import { useCallback, useEffect, useState } from 'react';
import { MapPin, Calendar, Users, Building2, Bed, CheckCircle, XCircle, ChevronDown, ChevronUp, Moon, Hotel, Hash, CalendarDays, ChevronLeft, ChevronRight, AlertTriangle, Send, MessageSquareText, Loader2, Plane } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { useAuth } from 'contexts/AuthContext';
import { FullScreenLoading } from 'components/ui/loader';
import { approveVenueAllocation, ApproveVenueAllocationRequest, getFaizRecommendedDates, getHotelRecommendedDates, getTourChats, getVenueApprovalDetailsPublicId, RecommendDatesPayload, sendRecommendDates, TourChat, TourChatSubmissionPayload, tourChatsubmittion } from 'services/venueApprovalService';
import axios from 'axios';
import { useRef } from "react";
import ConfirmDialog from 'ui/ConfirmDialog';
import { usePageHeader } from 'contexts/PageHeaderContext';
import { FormatDate, FormatDisplayDate } from 'utils/commonutils';

interface DateAvailability {
  rooms: number;
  roomTypes: {
    venueName: string;
    propertyName: string;
    roomType: string;
    available: number;
    bookedRooms: number;
    capacity: number;
  }[];
  venueApplicants?: {
    venueId: number;
    venueName: string;
    applicants: number;
  }[];
}

interface FaizRecommendedDateCombination {
  date: string;
  venues?: Array<{
    venueId: number;
    venueName: string;
    properties?: Array<{
      propertyId: number;
      propertyName: string;
      roomTypes?: Array<{
        propertyRoomTypeId: number;
        roomTypeName: string;
        capacity: number;
        availableRooms: number;
        bookedRooms: number;
      }>;
    }>;
  }>;
}

interface HotelStayingDateCombination {
  date: string;
  venues?: Array<{
    venueId: number;
    venueName: string;
    applicants: number;
  } | null>;
}

interface PropertyAvailabilityss {
  venueName: string;
  propertyName: string;
  roomTypes: {
    roomType: string;
    available: number;
    bookedRooms: number;
    capacity: number;
  }[];
}

const getPropertyAvailability = (
  roomTypes: DateAvailability["roomTypes"]
): PropertyAvailabilityss[] => {

  const grouped: Record<string, PropertyAvailabilityss> = {};

  roomTypes.forEach(room => {

    const key = `${room.venueName}-${room.propertyName}`;

    if (!grouped[key]) {
      grouped[key] = {
        venueName: room.venueName,
        propertyName: room.propertyName,
        roomTypes: []
      };
    }

    grouped[key].roomTypes.push({
      roomType: room.roomType,
      available: room.available ?? 0,
      bookedRooms: room.bookedRooms,
      capacity: room.capacity,
    });

  });

  return Object.values(grouped);
};

interface RecommendDatesDialogProps {
  tourId: string | number,
  mode: "faiz" | "hotel";
  startDate: string,
  availabilityByDate: Record<string, DateAvailability>;
  onMonthChange: (mode: "faiz" | "hotel", monthYear: string) => void;
  onClose: () => void;
  onConfirm: (mode: "faiz" | "hotel", startDate: string, endDate: string, reason: string) => void;
  nights: number;
  isSubmittingRecommendation: boolean;
  isLoadingMonth: boolean;
  tourStartDate: string;
  tourEndDate: string;
  venues: Venue[];
}

function RecommendDatesDialog({ tourId, mode, startDate, availabilityByDate, onMonthChange, onClose, onConfirm, nights, isSubmittingRecommendation, isLoadingMonth, tourStartDate, tourEndDate, venues }: RecommendDatesDialogProps) {
  // const today = new Date(2026, 6, 1); // June 2026 as starting point
  // const [viewYear, setViewYear] = useState(today.getFullYear());
  // const [viewMonth, setViewMonth] = useState(today.getMonth());

  const initialDate = startDate ? new Date(startDate) : new Date();
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  const minSelectableDate = new Date(initialDate);
  minSelectableDate.setHours(0, 0, 0, 0);

  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [recommendReason, setRecommendReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [hasInterVenueChanges, setHasInterVenueChanges] = useState(false);

  const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y: number, m: number) => new Date(y, m, 1).getDay();

  const changeMonth = (offset: number) => {
    const next = new Date(viewYear, viewMonth + offset, 1);
    const nextYear = next.getFullYear();
    const nextMonth = next.getMonth();
    setViewYear(nextYear);
    setViewMonth(nextMonth);
    setSelectedStart(null);
    onMonthChange(mode, `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}`);
  };

  const formatDateKey = (y: number, m: number, d: number) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const getEndDate = (start: string, n: number) => {
    const d = new Date(`${start}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };

  const getShiftedVenueDate = (venueDate?: string) => {
    if (!selectedStart || !tourStartDate || !venueDate) return venueDate ?? "";

    const originalTourStart = new Date(`${tourStartDate.slice(0, 10)}T00:00:00Z`);
    const originalVenueDate = new Date(`${venueDate.slice(0, 10)}T00:00:00Z`);
    const selectedTourStart = new Date(`${selectedStart}T00:00:00Z`);
    const offsetInDays = Math.round(
      (originalVenueDate.getTime() - originalTourStart.getTime()) / 86_400_000,
    );

    selectedTourStart.setUTCDate(selectedTourStart.getUTCDate() + offsetInDays);
    return selectedTourStart.toISOString().slice(0, 10);
  };

  const getInterVenueReason = () => {
    const venueLines = venues.map((venue) =>
      `${venue.name}: ${FormatDisplayDate(getShiftedVenueDate(venue.checkInDate))} → ${FormatDisplayDate(getShiftedVenueDate(venue.checkOutDate))}`,
    );

    return ["Here are the new suggested dates for venues:", ...venueLines].join("\n");
  };

  const handleInterVenueChanges = (checked: boolean) => {
    const generatedReason = getInterVenueReason();
    setHasInterVenueChanges(checked);

    if (checked) {
      setRecommendReason(generatedReason);
      setReasonError("");
    } else {
      setRecommendReason((currentReason) =>
        currentReason === generatedReason ? "" : currentReason,
      );
    }
  };

  const isInRange = (dateKey: string) => {
    if (!selectedStart) return false;
    const end = getEndDate(selectedStart, nights);
    return dateKey > selectedStart && dateKey < end;
  };

  const isEndDate = (dateKey: string) => {
    if (!selectedStart) return false;
    return dateKey === getEndDate(selectedStart, nights);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const calendarRowCount = Math.ceil((firstDay + daysInMonth) / 7);
  const monthName = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleDayClick = (dateKey: string) => {
    if (!availabilityByDate[dateKey]) return;
    setSelectedStart(dateKey);
    // if (!selectedStart) {
    //   setSelectedStart(dateKey);
    // }
    //  else {
    //   onConfirm(selectedStart, getEndDate(selectedStart, nights));
    // }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-2 sm:p-4">
      <div className="my-auto w-full max-w-lg overflow-visible rounded-xl bg-white shadow-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-3 sm:px-6 sm:py-4" style={{ backgroundColor: '#EBF5FB' }}>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" style={{ color: '#5B9BD5' }} />
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{mode === "faiz" ? "Recommend Faiz New Dates" : "Recommend Hotel New Dates"}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/60 rounded-lg transition-colors">
            <XCircle className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-3 sm:p-6">
          {/* <p className="text-sm text-gray-600 mb-4">
            Select a new <strong>start date</strong> from the dates with available rooms below.
            The system will auto-calculate the <strong>{nights}-night</strong> stay.
          </p> */}
          <p className="text-sm text-gray-600 mb-4">
            {mode === "faiz"
              ? "Select a suggested start date based on Faiz room availability."
              : "Select a suggested hotel start date. Hover a date to see the applicants staying at each venue."}
            {" "}The system will auto-calculate the <strong>{nights}-night</strong> stay.
          </p>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button disabled={isLoadingMonth} onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:cursor-not-allowed disabled:opacity-50">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="font-semibold text-gray-900 text-sm">{monthName}</span>
            <button disabled={isLoadingMonth} onClick={() => changeMonth(1)} className="p-1.5 hover:bg-gray-100 rounded-lg disabled:cursor-not-allowed disabled:opacity-50">
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          <div className="relative">
            {isLoadingMonth && (
              <div className="absolute inset-0 z-40 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-[1px]">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-50 shadow-sm ring-1 ring-blue-100">
                    <div className="absolute inset-1 animate-ping rounded-full bg-blue-100/60" />
                    <Loader2 className="relative h-6 w-6 animate-spin text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Checking availability...</p>
                    {/* <p className="mt-0.5 text-xs text-slate-500">Checking availability...</p> */}
                  </div>
                </div>
              </div>
            )}

            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-y-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateKey = formatDateKey(viewYear, viewMonth, day);
                const avail = availabilityByDate[dateKey];
                const propertyAvailability = avail ? getPropertyAvailability(avail?.roomTypes) : [];
                const venueApplicants = avail?.venueApplicants ?? [];
                const calendarColumn = (firstDay + i) % 7;
                const calendarRow = Math.floor((firstDay + i) / 7);
                const tooltipHorizontalClass = calendarColumn <= 1
                  ? "left-0"
                  : calendarColumn >= 5
                    ? "right-0"
                    : "left-1/2 -translate-x-1/2";
                const tooltipVerticalClass = calendarRow >= calendarRowCount - 2
                  ? "bottom-full mb-2"
                  : "top-full mt-2";
                const isStart = selectedStart === dateKey;
                const inRange = isInRange(dateKey);
                const isEnd = isEndDate(dateKey);
                const isCurrentTourDate = Boolean(
                  tourStartDate &&
                  tourEndDate &&
                  dateKey >= tourStartDate.slice(0, 10) &&
                  dateKey <= tourEndDate.slice(0, 10),
                );
                const isSelectedRangeDate = isStart || inRange || isEnd;
                // const today2 = new Date(2026, 5, 1);
                // const thisDate = new Date(viewYear, viewMonth, day);
                // const isPast = thisDate < today2;

                const thisDate = new Date(viewYear, viewMonth, day);
                thisDate.setHours(0, 0, 0, 0);
                const isPast = thisDate < minSelectableDate;


                return (
                  <div
                    key={day}
                    className={`relative text-center`}
                    // className="relative overflow-visible"
                    onMouseEnter={() => avail && setHoverDate(dateKey)}
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    <button
                      disabled={!avail || isPast}
                      onClick={() => handleDayClick(dateKey)}
                      className={`w-full aspect-square rounded-lg text-xs font-medium transition-all flex flex-col items-center justify-center leading-tight
                      ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                      ${!avail && !isPast ? 'text-gray-400 cursor-not-allowed bg-gray-50' : ''}
                      ${isCurrentTourDate && !isSelectedRangeDate ? 'bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-300' : ''}
                      ${avail && !isStart ? 'hover:bg-blue-100 cursor-pointer' : ''}
                      ${isStart ? 'text-white font-bold' : ''}
                      ${inRange ? 'bg-sky-100 text-sky-800 ring-1 ring-inset ring-sky-200' : ''}
                      ${isEnd && selectedStart ? 'bg-[#5B9BD5] text-white ring-1 ring-inset ring-blue-500 font-bold' : ''}
                    `}
                      style={isStart ? { backgroundColor: '#5B9BD5' } : {}}
                    >
                      <span>{day}</span>
                      {/* {avail && (
                      <span className={`text-[8px] leading-none ${isStart ? 'text-blue-100' : 'text-green-600'}`}>
                        {avail.rooms}r
                      </span>
                    )} */}
                      {mode === "faiz" && avail && (
                        <span className={`text-[8px] leading-none ${isStart || isEnd ? 'text-blue-100' : 'text-green-600'}`}>
                          {avail.rooms}r
                        </span>
                      )}
                    </button>

                    {/* ROOM AVAILABILITY POPUP */}
                    {mode === "faiz" && hoverDate === dateKey && propertyAvailability.length > 0 && (
                      <div className={`absolute z-50 w-[min(20rem,calc(100vw-2rem))] max-h-[min(20rem,60vh)] overflow-y-auto rounded-lg border border-blue-100 bg-white p-3 text-left shadow-xl ${tooltipHorizontalClass} ${tooltipVerticalClass}`}>
                        <div className="border-b border-blue-100 pb-2 mb-2">
                          <div className="font-semibold text-sm text-gray-900">
                            Property Room Availability
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {new Date(dateKey).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} - {avail.rooms} rooms total
                          </div>
                        </div>

                        <div className="space-y-3">
                          {propertyAvailability.map((property) => (
                            <div
                              key={`${property.venueName}-${property.propertyName}`}
                              className="space-y-1.5"
                            >
                              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                                <MapPin className="w-3 h-3" />
                                <span>{property.venueName}</span>
                              </div>

                              <div className="rounded-md bg-gray-50 p-2">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 mb-1.5">
                                  <Building2 className="w-3 h-3 text-gray-500" />
                                  <span>{property.propertyName}</span>
                                </div>

                                <div className="space-y-1">
                                  {property.roomTypes.map((room) => (
                                    <div
                                      key={room.roomType}
                                      className="flex items-center justify-between gap-3 text-xs"
                                    >
                                      <span className="text-gray-700">{room.roomType}</span>

                                      <span className="font-semibold text-green-600 whitespace-nowrap">
                                        {room.available} available
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* HOTEL BOOKING DATES */}
                    {mode === "hotel" && hoverDate === dateKey && avail && (
                      <div className={`absolute z-50 w-[min(16rem,calc(100vw-2rem))] max-h-[min(20rem,60vh)] overflow-y-auto rounded-lg border border-blue-100 bg-white p-3 text-left shadow-xl ${tooltipHorizontalClass} ${tooltipVerticalClass}`}>
                        <div className="border-b border-blue-100 pb-2 mb-2">
                          <div className="font-semibold text-sm text-gray-900">Applicants Staying</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {new Date(dateKey).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {venueApplicants.map((item) => (
                            <div key={item.venueId} className="flex items-center justify-between gap-3 text-xs">
                              <span className="text-gray-600">{item.venueName}</span>
                              <span className="font-semibold text-blue-700 whitespace-nowrap">
                                {item.applicants} applicants
                              </span>
                            </div>
                          ))}
                          {venueApplicants.length === 0 && (
                            <p className="text-xs text-gray-500">No applicant stays for this date.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-amber-100 ring-1 ring-inset ring-amber-300" />
              <span>Current tour dates</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#5B9BD5' }} />
              <span>Selected start</span>
            </div>
            {/* <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-blue-50 border border-blue-200" />
              <span>Stay range</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-100" />
              <span>Unavailable</span>
            </div> */}
            {selectedStart && (
              <label className="flex cursor-pointer items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={hasInterVenueChanges}
                  onChange={(event) => handleInterVenueChanges(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-[#5B9BD5]"
                />
                <span>Inter-Venue Changes</span>
              </label>
            )}
          </div>

          {selectedStart && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="font-medium text-blue-800">
                Selected: {FormatDisplayDate(selectedStart)} {" → "} {FormatDisplayDate(getEndDate(selectedStart, nights))}
              </p>
              {/* {availableRooms && (
                <p className="text-blue-600 text-xs mt-0.5">{availableRooms} rooms available on start date</p>
              )} */}
            </div>
          )}

          {/* recommended reason input */}
          {selectedStart && (
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Reason <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={3}
                value={recommendReason}
                onChange={(e) => {
                  setRecommendReason(e.target.value);
                  if (reasonError) setReasonError("");
                }}
                placeholder="Enter the reason for recommending these dates..."
                className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${reasonError
                  ? "border-red-500 focus:ring-red-200"
                  : "border-gray-300 focus:ring-blue-200"
                  }`}
              />

              {reasonError && (
                <p className="mt-1 text-xs text-red-600">
                  {reasonError}
                </p>
              )}
            </div>
          )}
          {/* recommended reason input */}
        </div>

        <div className="flex flex-col gap-2 px-3 pb-3 sm:flex-row sm:gap-3 sm:px-6 sm:pb-5">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            disabled={!selectedStart || isSubmittingRecommendation}
            onClick={() => {
              if (!selectedStart) return;

              if (!recommendReason.trim()) {
                setReasonError("Reason is required.");
                return;
              }

              onConfirm(
                mode,
                selectedStart,
                getEndDate(selectedStart, nights),
                recommendReason.trim()
              );
            }}
            className="flex-1 text-white"
            style={{ backgroundColor: selectedStart ? '#5B9BD5' : undefined }}
          >
            {isSubmittingRecommendation ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Recommend These Dates"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
// RECOMMENDED DATE CALENDER CODE END


interface VenueApprovalViewPageProps {
  onNavigate: (page: string) => void;
}

interface OtherTORequest {
  publicId?: string;
  tourPublicId?: string;
  toName: string;
  tourName?: string;
  tourCode: string;
  roomsRequested?: number;
  totalRequestedRooms?: number;
  roomsApplicants?: number;
  dates: string[];
}

interface RoomType {
  // roomId?: string,
  roomTypeId?: string | number,
  propertyRoomTypeId?: string | number,
  requestedRooms?: number;
  roomAllocated?: number | string,
  type: string;
  minCapacity: number;
  maxCapacity: number;
  dailyAvailability: { [date: string]: number };
  otherTORequests?: OtherTORequest[];
}

interface OtherTORequestModalData {
  propertyName: string;
  propertyGrade: string;
  propertyLocation: string;
  roomTypeName: string;
  minCapacity: number;
  maxCapacity: number;
  overallAvailable: number;
  requests: OtherTORequest[];
  requestUnit?: "rooms" | "applicants";
}

interface Property {
  id?: string;
  propertyId?: string | number;
  propertyPublicId?: string;
  name: string;
  venue?: string;
  grade: string;
  location: string;
  roomTypes: RoomType[];
  otherTORequests?: OtherTORequest[];
}

interface Venue {
  id: string;
  name: string;
  checkInDate?: string,
  checkOutDate?: string,
  stayingApplicants?: Array<{
    date: string;
    totalApplicants: number;
  }>;
  propertyCount: number;
  roomTypeCount: number;
  properties: Property[];
}

interface TourData {
  id: string;
  status: number;
  approvalStatus?: number;
  publicId?: string;
  tourCode: string;
  requestedStartDate?: string;
  tourName: string;
  tourType: string;
  tourCategory?: number;
  totalProperties?: string | number;
  otherTOsCount?: string | number;
  faizAccommodation?: boolean;
  accommodationPreference?: 'minimum' | 'maximum' | number;
  accommodationPreferenceName?: string;
  specialAccommodationRequest?: string;
  venue: string;
  venues: Venue[];
  startDate: string;
  endDate: string;
  nights: number;
  totalApplicants: number;
  totalRoomsRequested: number;
  bookingFlow: string;
  properties?: Property[];
  validations: {
    type: 'error' | 'warning' | 'info';
    message: string;
  }[];
  createdBy: string;
  createdDate: string;
}

interface ReviewMessage {
  id: string;
  isAdmin: string;
  sender: 'backoffice' | 'tour_operator';
  senderName: string;
  message: string;
  timestamp: string;
  dateTime: string;
}

const getOverallAvailability = (dailyAvailability: { [date: string]: number }) => {
  const values = Object.values(dailyAvailability);
  return Math.min(...values);
};

const getMinTotalCapacity = (roomType: RoomType) => {
  const overallAvailable = getOverallAvailability(roomType.dailyAvailability);
  return overallAvailable * roomType.minCapacity;
};

const getMaxTotalCapacity = (roomType: RoomType) => {
  const overallAvailable = getOverallAvailability(roomType.dailyAvailability);
  return overallAvailable * roomType.maxCapacity;
};

const getApprovedRoomKey = (
  venueId: string,
  propertyId: string | number | undefined,
  propertyRoomTypeId: string | number | undefined,
) => `${venueId}-${propertyId ?? ""}-${propertyRoomTypeId ?? ""}`;

export function VenueApprovalViewPage({ onNavigate }: VenueApprovalViewPageProps) {
  const { user } = useAuth();
  const { tourId = "1" } = useParams();
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [isSubmittingRecommendation, setIsSubmittingRecommendation] = useState(false);
  const [tour, setTour] = useState<TourData | null>(null);
  // const canTakeAction = [2, 3, 4].includes(Number(tour?.status));
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  const [approvedRooms, setApprovedRooms] = useState<{ [key: string]: string }>({});
  const [recommendationMode, setRecommendationMode] = useState<"faiz" | "hotel" | null>(null);


  // RECOMMEND DATE STATS
  const [recommendedDates, setRecommendedDates] = useState<Record<string, DateAvailability>>({});
  const [loadingRecommendedDates, setLoadingRecommendedDates] = useState(false);
  const [loadingCalendarMonth, setLoadingCalendarMonth] = useState(false);
  const [showRecommendDialog, setShowRecommendDialog] = useState(false);
  const [selectedOtherTORequests, setSelectedOtherTORequests] = useState<OtherTORequestModalData | null>(null);
  const [tourSwitchRequest, setTourSwitchRequest] = useState<OtherTORequest | null>(null);
  const [isTourOperatorRequestOpen, setIsTourOperatorRequestOpen] = useState(false);
  const [reviewConversation, setReviewConversation] = useState<ReviewMessage[]>([]);
  const [responseToTourOperator, setResponseToTourOperator] = useState('');


  // const tour = mockTourDataByID[tourId] || mockTourDataByID['1'];

  useEffect(() => {
    setPageHeader({
      title: "Venue Approval",
      icon: <Building2 className="h-4 w-4" />,
      onBack: () => onNavigate("/venue-approval"),
    });

    return () => setPageHeader(null);
  }, [onNavigate, setPageHeader]);


  const toggleProperty = (propertyId: string) => {
    setExpandedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  const handleConfirmTourSwitch = () => {
    if (!tourSwitchRequest?.tourPublicId) return;
    const { tourPublicId } = tourSwitchRequest;
    setTourSwitchRequest(null);
    onNavigate(`/venue-approval/view/${tourPublicId}`);
  };


  const handleApprove = async () => {
    if (isConfirmed) return;
    if (approvedRoomsValidationMessage) {
      return;
    }

    try {

      const payload: ApproveVenueAllocationRequest = {
        tourCategory: Number(tour?.tourCategory),
        venueRequests: isExternalTour
          ? ([{
            venueId: 0,
            propertyId: 0,
            roomRequests: [{
              roomId: 0,
              roomAllocated: 0,
            }],
          }])
          : (tour?.venues ?? []).flatMap((venue) =>
            (venue.properties ?? []).map((property) => ({
              venueId: Number(venue.id),
              propertyId: Number(property?.propertyId),
              roomRequests: (property.roomTypes ?? []).map((roomType) => ({
                roomId: Number(roomType?.propertyRoomTypeId),
                roomAllocated: Number(
                  approvedRooms[
                  getApprovedRoomKey(venue.id, property.propertyId, roomType.propertyRoomTypeId)
                  ] ?? 0,
                ),
              })),
            }))
          ),
      };
      // console.log("payload", payload)

      // VALIDATE APPROVED ROOM INPUT ATLEAST ONE
      const hasApprovedRooms = Object.values(approvedRooms).some(
        (value) => Number(value) > 0
      );

      if (!isExternalTour && !hasApprovedRooms) {
        toast.error("Please enter the approved room count for at least one room type.");
        return;
      }
      // VALIDATE APPROVED ROOM INPUT ATLEAST ONE


      await approveVenueAllocation(
        String(tour?.publicId || tourId), // publicId or tourId
        Number(user?.id), // logged-in user id
        payload
      );
      setTour((current) => current ? { ...current, approvalStatus: 2 } : current);

      toast.success("Venue Approved", {
        description: `Tour ${tour?.tourCode} venue allocation has been approved successfully.`,
      });

      setTimeout(() => {
        onNavigate("venue-approval");
      }, 1500);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to approve venue allocation.";
        toast.error(message);
      }
    }
  };


  const handleRecommendDates = async (
    mode: "faiz" | "hotel",
    startDate: string,
    endDate: string,
    reason: string
  ) => {
    if (isConfirmed) return;
    try {
      // console.log(mode, startDate, endDate, reason, tour?.publicId, tourId)
      setIsSubmittingRecommendation(true);

      const payload: RecommendDatesPayload = {
        startDate,
        endDate,
        reason,
        type: mode === "faiz" ? 1 : 2,
      };

      const response = await sendRecommendDates(
        String(tour?.publicId || tourId),
        payload
      );

      if (!response?.success) {
        toast.error(response.message);
        return;
      }

      toast.success(
        `${mode === "faiz" ? "Faiz" : "Hotel"
        } recommended dates sent successfully.`
      );

      setShowRecommendDialog(false);
      setRecommendationMode(null);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.data) {
        toast.error(
          error.response.data.message ||
          "Failed to send recommended dates."
        );
      } else {
        toast.error("Failed to send recommended dates.");
      }
    } finally {
      setIsSubmittingRecommendation(false);
    }
  };



  const hasErrors = tour?.validations?.some(v => v.type === 'error') ?? false;

  const formatShortDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  const getRequestAmount = (request: OtherTORequest) =>
    request.roomsApplicants ?? request.roomsRequested ?? 0;

  const getTOType = (tourType?: string, tourCategory?: number, bookingFlow?: string) => {
    const source = `${tourType || ''} ${bookingFlow || ''}`.toLowerCase();
    if (Number(tourCategory) === 4 || source.includes('external')) return 'External';
    if (source.includes('instant')) return 'Instant';
    if (source.includes('miqaat')) return 'Miqaat';
    return 'Faiz';
  };


  const totalOtherTOs = tour?.venues?.reduce((venueSum, venue) => venueSum + (venue.properties ?? []).reduce(
    (propertySum, property) =>
      propertySum +
      (property.roomTypes ?? []).reduce((roomSum, room) =>
        roomSum + (room.otherTORequests?.length ?? 0),
        0
      ),
    0
  ),
    0
  ) ?? 0;


  const accommodationPreferenceLabel = tour?.accommodationPreferenceName
    || (tour?.accommodationPreference === 'minimum' || tour?.accommodationPreference === 1
      ? 'Min Occupancy'
      : 'Max Occupancy');
  const specialAccommodationRequest = tour?.specialAccommodationRequest?.trim() || 'No special request';
  const latestReviewMessage = reviewConversation[reviewConversation.length - 1];
  const hasNewTourOperatorMessage = latestReviewMessage?.sender === 'tour_operator';
  const chatContainerRef = useRef<HTMLDivElement>(null);


  // SCROLL TO TOP EFFECT ON CHAT
  useEffect(() => {
    if (reviewConversation.length > 0) {
      chatContainerRef.current?.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [reviewConversation]);
  // SCROLL TO TOP EFFECT ON CHAT


  const isConfirmed = tour?.approvalStatus === 2;
  const tourStatusLabel = isConfirmed ? "Confirmed" : ({
    1: "Draft",
    2: "Submitted",
    3: "Alternative Dates Proposed",
    4: "Resubmitted",
    5: "Confirmed",
    6: "Cancelled",
    7: "Checked In",
    8: "Checked Out",
    9: "Completed",
  } as Record<number, string>)[tour?.status ?? 0] ?? "Unknown";
  const isExternalTour =
    Number(tour?.tourCategory) === 4 ||
    String(tour?.bookingFlow || "").toLowerCase().includes("external") ||
    String(tour?.tourType || "").toLowerCase().includes("external");

  const isInstantTour = !isExternalTour && (
    Number(tour?.tourCategory) === 1 ||
    getTOType(tour?.tourType, tour?.tourCategory, tour?.bookingFlow) === "Instant"
  );

  const hasApprovedRoomAllocation = isExternalTour || Object.values(approvedRooms).some(
    (value) => Number(value) > 0,
  );

  const totalApprovedRooms = Object.values(approvedRooms).reduce(
    (total, value) => total + Number(value || 0),
    0,
  );
  const applicantCount = Number(tour?.totalApplicants ?? 0);
  const approvedRoomsValidationMessage = !isExternalTour && tour && totalApprovedRooms > applicantCount
    ? `Approved rooms total (${totalApprovedRooms}) cannot exceed the applicant count (${applicantCount}). Please reduce the approved rooms.`
    : "";
  const cannotApprove = isConfirmed || isInstantTour || hasErrors || !hasApprovedRoomAllocation || Boolean(approvedRoomsValidationMessage);


  //PREFIELD API CALL
  const errorCount =
    tour?.validations?.filter((validation) => validation.type === "error")
      .length ?? 0;

  const warningCount =
    tour?.validations?.filter((validation) => validation.type === "warning")
      .length ?? 0;

  const infoCount =
    tour?.validations?.filter((validation) => validation.type === "info")
      .length ?? 0;


  // PREFILLED DATA API SET
  const loadVenueApprovalDetailsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVenueApprovalDetailsPublicId(String(tourId));
      if (response?.success === false) {
        throw new Error(response.message || "Failed to load Venue approval details");
      }

      const data = (response?.data ?? response) as TourData;
      setTour(data);
      setApprovedRooms(
        (data.venues ?? []).reduce<Record<string, string>>((rooms, venue) => {
          (venue.properties ?? []).forEach((property) => {
            (property.roomTypes ?? []).forEach((roomType) => {
              rooms[
                getApprovedRoomKey(venue.id, property.propertyId, roomType.propertyRoomTypeId)
              ] = String(roomType.roomAllocated ?? "");
            });
          });
          return rooms;
        }, {}),
      );
    } catch {
      toast.error("Failed to load Venue approval details");
    } finally {
      setLoading(false);
    }
  }, [tourId]);

  useEffect(() => {
    if (tourId) {
      loadVenueApprovalDetailsData();
    }
  }, [tourId, loadVenueApprovalDetailsData]);
  // PREFILLED API DATA


  // RECOMMENDED DATE  API RESPONSE MAPPING FUNCTION START
  const mapFaizRecommendedDates = (
    combinations: FaizRecommendedDateCombination[] = [],
  ): Record<string, DateAvailability> => {
    const result: Record<string, DateAvailability> = {};

    combinations.forEach((combination) => {
      const roomTypes = (combination.venues ?? []).flatMap((venue) =>
        (venue.properties ?? []).flatMap((property) =>
          (property.roomTypes ?? []).map((room) => ({
            venueName: venue.venueName,
            propertyName: property.propertyName,
            roomType: room.roomTypeName,
            available: room.availableRooms ?? 0,
            bookedRooms: room.bookedRooms ?? 0,
            capacity: room.capacity ?? 0,
          })),
        ),
      );

      const rooms = roomTypes.reduce((sum, room) => sum + room.available, 0);
      if (roomTypes.length > 0 && rooms > 0) {
        result[combination.date] = { rooms, roomTypes };
      }
    });

    return result;
  };

  const mapHotelRecommendedDates = (
    combinations: HotelStayingDateCombination[] = [],
  ): Record<string, DateAvailability> => {
    const result: Record<string, DateAvailability> = {};

    combinations.forEach((combination) => {
      const venueApplicants = (combination.venues ?? []).filter(
        (venue): venue is NonNullable<typeof venue> => venue !== null,
      );

      result[combination.date] = {
        rooms: 0,
        roomTypes: [],
        venueApplicants,
      };
    });

    return result;
  };


  //FETCH CALENDER RECOMMANDED DATES FOR FAIZ AND HOTEL
  const fetchRecommendedDates = async (mode: "faiz" | "hotel", monthYear?: string) => {
    if (!tourId || isConfirmed) return;
    const isMonthChange = Boolean(monthYear);
    const monthLoadingStartedAt = Date.now();

    try {
      if (isMonthChange) {
        setLoadingCalendarMonth(true);
      } else {
        setLoadingRecommendedDates(true);
      }

      const requestedMonth = monthYear ?? (tour?.requestedStartDate || tour?.startDate || new Date().toISOString())
        .slice(0, 7);
      let response;

      if (mode === "faiz") {
        response = await getFaizRecommendedDates(
          String(tourId),
          requestedMonth,
        );
      } else {
        response = await getHotelRecommendedDates(
          String(tourId),
          requestedMonth,
        );
      }

      if (!response?.success) {
        toast.error(response.message);
        return;
      }

      const mappedDates = mode === "faiz"
        ? mapFaizRecommendedDates(response.data?.recommendedDateCombinations)
        : mapHotelRecommendedDates(response.data?.stayingDateCombinations);

      setRecommendedDates(mappedDates);
      setShowRecommendDialog(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        toast.error(
          error.response.data.message ||
          "Failed to load recommended dates."
        );
      }
    } finally {
      if (isMonthChange) {
        const remainingIndicatorTime = Math.max(
          0,
          500 - (Date.now() - monthLoadingStartedAt),
        );
        if (remainingIndicatorTime > 0) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, remainingIndicatorTime);
          });
        }
        setLoadingCalendarMonth(false);
      } else {
        setLoadingRecommendedDates(false);
      }
    }
  };
  // RECOMMENDED DATE FUNCTION END


  // CHAT SECTION HANDLE SUBMIT FUNCTION
  const loadTourChats = useCallback(async (showError = false) => {
    try {
      const response = await getTourChats(tourId);
      const chats = response?.data?.map((item: TourChat) => ({
        isAdmin: item.isAdmin,
        id: item.publicId,
        sender: item.isInternal ? "backoffice" : "tour_operator",
        senderName: item.senderName,
        message: item.message,
        timestamp: new Date(item.createdOn).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        dateTime: new Date(item.createdOn).toLocaleDateString(),
      }));
      setReviewConversation(chats);
    } catch (error) {
      console.error(error);
      if (showError) {
        toast.error("Failed to load chat.");
      }
    }
  }, [tourId]);


  useEffect(() => {
    if (tourId) {
      loadTourChats(false);
    }
  }, [loadTourChats, tourId]);


  const handleSendResponse = async () => {
    const message = responseToTourOperator.trim();

    if (!message) {
      toast.error("Empty message is not allowed.");
      return;
    }

    try {
      setLoading(true);
      const payload: TourChatSubmissionPayload = {
        tourPublicId: tourId,
        message: message,
        isInternal: false,
      };
      const response = await tourChatsubmittion(payload);
      if (!response.success) {
        toast.error(response.message);
        return;
      }
      toast.success("Message sent successfully.");
      setResponseToTourOperator("");
      // Refresh chat
      await loadTourChats();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="space-y-6">
      {loading && <FullScreenLoading />}
      {loadingRecommendedDates && !showRecommendDialog && <FullScreenLoading />}

      {/* Tour Details */}
      <div className="rounded-xl border border-sky-100 bg-white shadow-sm overflow-hidden">
        <div className="h-1 bg-[#5B9BD5]" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_repeat(5,minmax(105px,1fr))] divide-y md:divide-y-0 md:divide-x divide-sky-100">
          <div className="px-4 py-3 min-w-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0 ring-1 ring-sky-100">
                <Calendar className="w-4 h-4" style={{ color: '#5B9BD5' }} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Tour Period</p>
                <p className="font-bold text-gray-950 text-sm leading-tight whitespace-nowrap">
                  {FormatDate(String(tour?.startDate))}  -  {FormatDate(String(tour?.endDate))}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-gray-600 leading-tight truncate">
                  <MapPin className="w-3 h-3 shrink-0 text-indigo-600" />
                  <span className="truncate">{tour?.venue}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Applicants</p>
            <div className="mt-1 flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color: '#5B9BD5' }} />
              <p className="text-lg font-bold text-gray-950 leading-none">{tour?.totalApplicants}</p>
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Nights</p>
            <div className="mt-1 flex items-center gap-2">
              <Moon className="w-4 h-4 text-cyan-600" />
              <p className="text-lg font-bold text-gray-950 leading-none">{tour?.nights}</p>
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Properties</p>
            <div className="mt-1 flex items-center gap-2">
              <Hotel className="w-4 h-4 text-blue-600" />
              <p className="text-lg font-bold text-gray-950 leading-none">{tour?.totalProperties}</p>
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Other TOs</p>
            <div className="mt-1 flex items-center gap-2">
              <Hash className="w-4 h-4 text-violet-600" />
              <p className="text-lg font-bold text-gray-950 leading-none">{tour?.otherTOsCount ?? totalOtherTOs}</p>
            </div>
          </div>

          <div className="px-4 py-3">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">TO Type</p>
            <div className="mt-1 flex items-center gap-2">
              <Hash className="w-4 h-4 text-violet-600" />
              <p className="text-sm font-bold text-gray-950 leading-none">
                {getTOType(tour?.tourType, tour?.tourCategory, tour?.bookingFlow)}
              </p>
            </div>

          </div>
        </div>


        <div className="grid grid-cols-1 gap-px border-t border-sky-100 bg-sky-100 md:grid-cols-2 xl:grid-cols-5 [&>*]:bg-white">
          <div className="flex min-w-0 items-center gap-3 overflow-hidden px-4 py-2.5">
            <div className="h-9 w-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0 ring-1 ring-sky-100">
              <Plane className="w-4 h-4" style={{ color: '#5B9BD5' }} />
            </div>
            <div className="mt-1 min-w-0">
              <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Tour</p>
              <h1 className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm font-bold leading-tight text-gray-950">
                <span className="min-w-0 break-words">{tour?.tourName}</span>
                <Badge className="shrink-0 whitespace-nowrap border-blue-300 bg-green-100 text-blue-700">{tour?.tourCode}</Badge>
              </h1>
            </div>
          </div>

          <div className="px-4 py-2.5 min-w-0">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Created By</p>
            <div className="mt-1 flex items-center gap-2 min-w-0">
              <Users className="w-4 h-4 text-slate-500 shrink-0" style={{ color: '#5B9BD5' }} />
              <p className="font-bold text-gray-950 text-sm leading-tight truncate">{tour?.createdBy}</p>
            </div>
          </div>

          <div className="px-4 py-2.5 min-w-0">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Accommodation Basis</p>
            <p className="mt-1 font-bold text-gray-950 text-sm leading-tight">
              {accommodationPreferenceLabel}
            </p>
          </div>

          <div className="px-4 py-2.5 min-w-0">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Special Request</p>
            <p className="font-bold text-gray-950 text-sm leading-tight break-words">
              {specialAccommodationRequest}
            </p>
          </div>

          <div className="px-4 py-2.5 min-w-0">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide">Status</p>
            <Badge
              variant="outline"
              className={`mt-1 whitespace-normal ${tourStatusLabel === "Confirmed"
                ? "border-green-300 bg-green-100 text-green-700"
                : tourStatusLabel === "Cancelled"
                  ? "border-red-300 bg-red-100 text-red-700"
                  : "border-blue-200 bg-blue-50 text-blue-700"}`}
            >
              {tourStatusLabel}
            </Badge>
          </div>
        </div>
      </div>


      {/* VALIDATION STATUS START */}
      <div
        className={`col-span-2 sm:col-span-3 lg:col-span-2 rounded-xl px-4 py-2 flex items-center gap-3 border ${errorCount > 0
          ? "bg-red-50 border-red-200"
          : warningCount > 0
            ? "bg-yellow-50 border-yellow-200"
            : "bg-green-50 border-green-200"
          }`}
      >
        <div
          className={`p-2.5 rounded-lg ${errorCount > 0
            ? "bg-red-100"
            : warningCount > 0
              ? "bg-yellow-100"
              : "bg-green-100"
            }`}
        >
          {errorCount > 0 ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : warningCount > 0 ? (
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-600" />
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">Validation</p>
          <p className={`font-semibold text-sm ${errorCount > 0 ? "text-red-700"
            : warningCount > 0 ? "text-yellow-700"
              : "text-green-700"}`}>

            {errorCount > 0
              ? `${errorCount} error${errorCount > 1 ? "s" : ""} found`
              : warningCount > 0
                ? `${warningCount} warning${warningCount > 1 ? "s" : ""}`
                : infoCount > 0
                  ? `${infoCount} info message${infoCount > 1 ? "s" : ""}`
                  : "All checks passed"}
          </p>

          {(tour?.validations || [])?.length > 0 && (
            <div className="mt-1 space-y-1">
              {tour?.validations.map((validation, index) => (
                <p
                  key={index}
                  className={`text-xs ${validation.type === "error"
                    ? "text-red-600"
                    : validation.type === "warning"
                      ? "text-yellow-700"
                      : "text-green-600"
                    }`}
                >
                  {validation.message}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* VALIDATION STATUS END */}

      {/* CHAT SECTION */}
      <div className="rounded-xl border border-sky-200 bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setIsTourOperatorRequestOpen(prev => !prev)}
          className="flex w-full items-center justify-between gap-3 border-b border-sky-100 bg-blue-50/60 px-4 py-2.5 text-left transition hover:bg-blue-50"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="relative h-8 w-8 rounded-lg bg-white flex items-center justify-center shrink-0 ring-1 ring-blue-100">
              <MessageSquareText className="w-4 h-4" style={{ color: '#5B9BD5' }} />
              {hasNewTourOperatorMessage && !isTourOperatorRequestOpen && (
                <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Tour Operator Request</h3>
              <p className="text-[11px] text-gray-600">
                {isTourOperatorRequestOpen
                  ? 'Conversation is open'
                  : 'Conversation between tour operator and admin'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hasNewTourOperatorMessage && (
              <Badge className="bg-red-100 text-red-700 border-red-300 text-[11px]">
                New message
              </Badge>
            )}
            <Badge className="bg-white text-[#5B9BD5] border-[#5B9BD5]/30 text-[11px]">
              {reviewConversation.length} messages
            </Badge>
            {isTourOperatorRequestOpen ? (
              <ChevronUp className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            )}
          </div>
        </button>

        {isTourOperatorRequestOpen && (
          <div className="p-3 md:p-4 space-y-3">
            {reviewConversation.length > 0 ? (
              <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/40 p-3">
                <div ref={chatContainerRef} className="space-y-2 max-h-[360px] overflow-y-auto pr-1 pb-2">
                  {reviewConversation.map((msg, index) => {
                    const isLatest = index === reviewConversation.length - 1;
                    const isAdmin = msg.isAdmin;
                    // const isAdmin = msg.sender === 'backoffice';

                    return (
                      <div
                        key={msg.id}
                        className={`flex px-0.5 ${isAdmin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[78%] rounded-2xl border px-3 py-2 shadow-sm ${isAdmin
                            ? 'rounded-br-sm border-blue-200 bg-white'
                            : 'rounded-bl-sm border-green-200 bg-green-50'
                            } ${isLatest ? 'ring-1 ring-red-300' : ''}`}
                        >
                          <div className="mb-1 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="truncate text-[11px] font-bold text-gray-900">
                                {msg.senderName}
                              </span>
                              <Badge
                                className={
                                  isAdmin
                                    ? 'bg-blue-100 text-blue-700 border-blue-300 text-[9px] px-1.5 py-0'
                                    : 'bg-green-100 text-green-700 border-green-300 text-[9px] px-1.5 py-0'
                                }
                              >
                                {isAdmin ? 'Admin' : 'TO'}
                              </Badge>
                              {isLatest && (
                                <Badge className="bg-red-100 text-red-700 border-red-300 text-[9px] px-1.5 py-0">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-[10px] font-semibold text-gray-500">
                                {msg.timestamp}
                              </p>
                              <p className="text-[9px] text-gray-400">
                                {msg.dateTime}
                              </p>
                            </div>
                          </div>
                          <p className="whitespace-pre-line text-xs leading-relaxed text-gray-800">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 border border-dashed border-gray-300">
                <p className="text-xs md:text-sm text-gray-600 text-center">
                  No conversation messages yet. The conversation will appear here once the review process begins.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-2">
              <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                <textarea
                  value={responseToTourOperator}
                  onChange={(e) => setResponseToTourOperator(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault(); // Prevent new line
                      handleSendResponse();
                    }
                  }}
                  placeholder="Type response to tour operator..."
                  className="h-10 min-h-10 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#5B9BD5]"
                />
                <Button
                  size="icon"
                  className="h-10 w-10 text-white"
                  style={{ backgroundColor: '#5B9BD5' }}
                  onClick={handleSendResponse}
                  disabled={!responseToTourOperator.trim()}
                  title="Send Response"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* CHAT SECTION */}

      {/* VENUE-WISE REQUESTS */}
      <div className="rounded-lg border border-gray-200 bg-white p-3 sm:p-6">
        <h3 className="mb-4 text-base font-semibold text-gray-900 sm:text-lg">{isExternalTour ? "Venue-wise Applicant Stay" : "Venue-wise Room Requests"}</h3>
        <div className="space-y-5">
          {tour?.venues?.length ? (
            <>
              {tour?.venues?.map((venue) => (
                <div key={venue.id} className="border border-blue-100 rounded-lg overflow-hidden">
                  {/* Venue Header */}
                  <div className="flex flex-col gap-3 border-b border-blue-100 bg-blue-50/70 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
                    <div className="flex min-w-0 items-start gap-2 sm:items-center sm:gap-3">
                      <div className="p-2 rounded-lg bg-white shrink-0">
                        <MapPin className="w-4 h-4" style={{ color: "#5B9BD5" }} />
                      </div>

                      <div className="min-w-0">
                        <h4 className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-gray-900 sm:gap-2 sm:text-base">
                          <span className="break-words">{venue.name}</span>

                          <span className="rounded-md bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 sm:px-2 sm:text-xs">
                            {FormatDate(String(venue?.checkInDate))} – {FormatDate(String(venue?.checkOutDate))}
                          </span>
                        </h4>

                        <p className="text-xs text-gray-600">
                          {isExternalTour ? (
                            "External Hotel"
                          ) : (
                            <>{venue.propertyCount} {venue.propertyCount === 1 ? "property" : "properties"} available for assignment</>
                          )}
                        </p>
                      </div>
                    </div>

                    <Badge className="w-fit shrink-0 border-blue-200 bg-white text-blue-700 sm:self-center">
                      {isExternalTour ? "0 applicants" : venue.roomTypeCount + " room types"}
                    </Badge>
                  </div>

                  {/* Properties */}
                  {/* EXTERNAL HOTELS MAPPING STARTS*/}
                  <div className="space-y-3 p-2.5 sm:p-4">
                    {isExternalTour ? (() => {
                      const externalRequests = (venue.properties ?? []).flatMap((property) =>
                        property.otherTORequests?.length
                          ? property.otherTORequests
                          : (property.roomTypes ?? []).flatMap((roomType) => roomType.otherTORequests || [])
                      );

                      return (
                        <div className="rounded-lg border border-blue-100 bg-slate-50 p-4" >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                              <div className="rounded-lg bg-blue-100 p-2">
                                <Building2 className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">External Hotel</h4>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              disabled={externalRequests.length === 0}
                              onClick={() => setSelectedOtherTORequests({
                                propertyName: "External Hotel",
                                propertyGrade: "External",
                                propertyLocation: venue.name,
                                roomTypeName: "Tour Request Summary on Similar Dates",
                                minCapacity: 0,
                                maxCapacity: 0,
                                overallAvailable: 0,
                                requests: externalRequests,
                                requestUnit: "applicants",
                              })}
                              className="border-purple-200 bg-white text-purple-700 hover:bg-purple-50"
                            >
                              TO Requests
                            </Button>
                          </div>

                          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Applicant Stay by Date</span>
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                              {(venue.stayingApplicants ?? []).map((stay) => (
                                <div key={stay.date} className="flex items-center justify-between gap-2 rounded-lg border border-sky-100 bg-sky-50/50 px-2.5 py-2">
                                  <span className="text-xs font-semibold text-slate-700">
                                    {formatShortDate(stay.date)}
                                  </span>
                                  <Badge className="border-emerald-200 bg-white px-1.5 py-0 text-[10px] text-emerald-700">
                                    <Users className="mr-1 h-3 w-3" /> {stay.totalApplicants} applicants
                                  </Badge>
                                </div>
                              ))}
                              {(venue.stayingApplicants ?? []).length === 0 && (
                                <p className="col-span-full py-3 text-center text-xs text-slate-500">
                                  No applicant stay data available.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                      // {/* EXTERNAL HOTELS MAPPING ENDS */}
                    })() :
                      // REGULAR PROPERTIES INSTANT AND FAIZ MAPPING STARTS
                      venue?.properties?.map((property, index) => {
                        const propertyKey = property.propertyPublicId;
                        const isExpanded = expandedProperties.has(String(propertyKey));
                        const propertyOtherTORequests = Object.values(
                          (property.roomTypes ?? [])
                            .flatMap((roomType) => roomType.otherTORequests ?? [])
                            .reduce<Record<string, OtherTORequest>>((requests, request) => {
                              const key = request.tourPublicId || request.publicId || request.tourCode;
                              const existing = requests[key];

                              if (!existing) {
                                requests[key] = {
                                  ...request,
                                  roomsRequested: request.roomsRequested ?? 0,
                                  totalRequestedRooms: request.roomsRequested ?? 0,
                                  dates: [...request.dates],
                                };
                              } else {
                                const roomsRequested = (existing.roomsRequested ?? 0) + (request.roomsRequested ?? 0);
                                requests[key] = {
                                  ...existing,
                                  roomsRequested,
                                  totalRequestedRooms: roomsRequested,
                                  dates: Array.from(new Set([...existing.dates, ...request.dates])),
                                };
                              }

                              return requests;
                            }, {}),
                        );

                        return (
                          <div
                            key={property.id || index}
                            className="border rounded-lg overflow-hidden"
                          >
                            {/* Keep your existing property UI */}
                            <div key={property.id} className="border rounded-lg overflow-hidden">
                              {/* Property Header */}
                              <div
                                className="cursor-pointer bg-gray-50 p-3 transition-colors hover:bg-gray-100 sm:p-4"
                                onClick={() => toggleProperty(String(propertyKey))}
                              >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center sm:gap-3">
                                    <div className="shrink-0 rounded-lg bg-blue-100 p-2">
                                      <Building2 className="w-4 h-4" style={{ color: '#5B9BD5' }} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <h4 className="break-words text-sm font-semibold leading-snug text-gray-900 sm:text-lg">{property.name}</h4>
                                      <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs">{property.grade}</Badge>
                                        <span className="text-sm text-gray-600">{property.location}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-2 sm:justify-end sm:gap-4 sm:border-0 sm:pt-0">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      disabled={propertyOtherTORequests.length === 0}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedOtherTORequests({
                                          propertyName: property.name,
                                          propertyGrade: property.grade,
                                          propertyLocation: property.location,
                                          roomTypeName: "All Room Types",
                                          minCapacity: 0,
                                          maxCapacity: 0,
                                          overallAvailable: 0,
                                          requests: propertyOtherTORequests,
                                        });
                                      }}
                                      className="border-purple-200 bg-white text-purple-700 hover:bg-purple-50"
                                    >
                                      TO Requests ({propertyOtherTORequests.length})
                                    </Button>
                                    <Button variant="ghost" size="sm">
                                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                                    </Button>
                                  </div>
                                </div>
                              </div>

                              {/* Property Details */}
                              {isExpanded && (
                                <div className="p-4 bg-white">
                                  <div className="z-[10] border rounded-lg overflow-hidden">
                                    <Table>
                                      <TableHeader>
                                        <TableRow style={{ backgroundColor: '#D6E9F8' }} className="border-b-2 border-blue-300">
                                          <TableHead className="font-semibold" style={{ color: '#5B9BD5' }}>Room Type</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Min Cap.</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Max Cap.</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Overall Available</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Min Total Capacity</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Max Total Capacity</TableHead>
                                          <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Approved Rooms</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {property.roomTypes.map((roomType, idx) => {
                                          const overallAvailable = getOverallAvailability(roomType.dailyAvailability);
                                          const minTotalCapacity = getMinTotalCapacity(roomType);
                                          const maxTotalCapacity = getMaxTotalCapacity(roomType);
                                          const roomKey = getApprovedRoomKey(
                                            venue.id,
                                            property.propertyId,
                                            roomType.propertyRoomTypeId,
                                          );

                                          return (
                                            <TableRow key={idx} className="hover:bg-gray-50">
                                              <TableCell>
                                                <div className="flex items-center gap-2">
                                                  <Bed className="w-4 h-4 text-gray-400" />
                                                  <span className="font-medium">{roomType.type}</span>
                                                </div>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <Badge variant="outline" className="text-xs">{roomType.minCapacity}</Badge>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <Badge variant="outline" className="text-xs">{roomType.maxCapacity}</Badge>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <Popover>
                                                  <PopoverTrigger asChild>
                                                    <Badge className="bg-blue-100 text-blue-700 cursor-pointer">
                                                      {overallAvailable}
                                                    </Badge>
                                                  </PopoverTrigger>
                                                  <PopoverContent
                                                    side="top"
                                                    align="center"
                                                    className="bg-white border shadow-xl p-0 rounded-xl"
                                                    style={{ width: "200px", maxWidth: "100vw" }}
                                                  >
                                                    {/* Your existing content */}
                                                    <div className="space-y-1 p-3">
                                                      <p className="font-semibold text-xs mb-2 text-center">Daily Availability:</p>
                                                      {Object.entries(roomType.dailyAvailability).map(([date, available]) => {
                                                        const dateObj = new Date(date);
                                                        const dayMonth = `${dateObj.getDate()} ${dateObj.toLocaleDateString('en-US', { month: 'short' })}`;
                                                        return (
                                                          <div key={date} className="flex items-center justify-between gap-3 text-xs">
                                                            <span className="text-gray-600">{dayMonth}:</span>
                                                            <span className="font-semibold text-blue-600">
                                                              {available} rooms
                                                            </span>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                    {/* Your existing content */}
                                                  </PopoverContent>
                                                </Popover>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <span className="text-gray-600 font-medium">{minTotalCapacity} pax</span>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <span className="text-gray-600 font-medium">{maxTotalCapacity} pax</span>
                                              </TableCell>
                                              <TableCell className="text-center">
                                                <input
                                                  type="number"
                                                  min={0}
                                                  max={overallAvailable}
                                                  placeholder="0"
                                                  // value={approvedRooms[`${property.id}-${idx}`] || ""}
                                                  value={approvedRooms[roomKey] ?? ""}
                                                  onChange={(e) => {
                                                    const value = Number(e.target.value);

                                                    // Allow clearing the input
                                                    if (e.target.value === "") {
                                                      setApprovedRooms((prev) => ({ ...prev, [roomKey]: "" }));
                                                      return;
                                                    }

                                                    // Prevent negative values
                                                    if (value < 0) {
                                                      toast.error("Value cannot be negative.");
                                                      return;
                                                    }

                                                    // Prevent values greater than overallAvailable
                                                    if (value > overallAvailable) {
                                                      toast.error(`Value cannot be greater than ${overallAvailable}.`);
                                                      return;
                                                    }

                                                    // Update state
                                                    setApprovedRooms((prev) => ({ ...prev, [roomKey]: e.target.value }));
                                                  }}
                                                  className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm text-center focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                                                  style={{ color: "#5B9BD5" }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    {/* // REGULAR PROPERTIES INSTANT AND FAIZ MAPPING END */}
                  </div>
                </div>
              ))}
            </>
          ) : (<>
            <div className="flex items-center justify-center py-12 text-gray-500">
              No venue data available.
            </div>
          </>)}
        </div>
        {approvedRoomsValidationMessage && (
          <Badge
            role="alert"
            className="mt-4 whitespace-normal border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
          >
            <AlertTriangle className="shrink-0" />
            {approvedRoomsValidationMessage}
          </Badge>
        )}
      </div>

      {/* Approval Actions */}
      <div className="sticky bottom-4 flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-lg sm:flex-row">
        <Button
          onClick={() => {
            setRecommendationMode("faiz");
            fetchRecommendedDates("faiz");
          }}
          disabled={isInstantTour || isConfirmed}
          variant="outline"
          className="flex-1 h-12 border-amber-400 font-medium text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          Recommend Faiz New Dates
        </Button>

        <Button
          onClick={() => {
            setRecommendationMode("hotel");
            fetchRecommendedDates("hotel");
          }}
          disabled={isInstantTour || isConfirmed}
          variant="outline"
          className="flex-1 h-12 border-blue-400 font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
        >
          <Hotel className="mr-2 h-4 w-4" />
          Recommend Hotel New Dates
        </Button>

        <Button
          onClick={handleApprove}
          disabled={cannotApprove}
          className="flex-1 h-12 text-base font-medium"
          style={{
            backgroundColor:
              cannotApprove ? "#9CA3AF" : "#22C55E",
          }}
          onMouseEnter={(e) => {
            if (!cannotApprove) {
              e.currentTarget.style.backgroundColor = "#16A34A";
            }
          }}
          onMouseLeave={(e) => {
            if (!cannotApprove) {
              e.currentTarget.style.backgroundColor = "#22C55E";
            }
          }}
        >
          <CheckCircle className="mr-2 h-4 w-4" />
          Approve Venue Allocation
        </Button>
      </div>


      <Dialog open={!!selectedOtherTORequests} onOpenChange={(open) => !open && setSelectedOtherTORequests(null)}>
        <DialogContent className="flex max-h-[94vh] w-[96vw] !max-w-4xl flex-col gap-0 overflow-hidden bg-white p-0 sm:w-[92vw] sm:!max-w-4xl lg:!max-w-4xl">
          <DialogHeader className="shrink-0 border-b border-gray-200 bg-sky-50/70 px-3 py-3 sm:px-5 sm:py-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:pr-8">
              <div>
                <DialogTitle className="pr-7 text-lg font-semibold leading-tight sm:text-xl" style={{ color: '#5B9BD5' }}>
                  {/* Other TO Room Requests */}
                  Tour Request Summary on Similar Dates
                </DialogTitle>
                {selectedOtherTORequests && (
                  <p className="text-sm text-gray-600 mt-1 max-w-5xl leading-snug">
                    {selectedOtherTORequests.propertyName} - {selectedOtherTORequests.roomTypeName} - {selectedOtherTORequests.propertyLocation}
                  </p>
                )}
              </div>
              {selectedOtherTORequests && (
                <div className="flex flex-wrap gap-2 md:justify-end">
                  <Badge className="bg-white text-blue-700 border-blue-200">{selectedOtherTORequests.propertyGrade}</Badge>
                  <Badge className="bg-white text-purple-700 border-purple-200">{selectedOtherTORequests.requests.length} TOs</Badge>
                  <Badge className="bg-white text-emerald-700 border-emerald-200">
                    {selectedOtherTORequests.requests.reduce((sum, req) => sum + getRequestAmount(req), 0)} {selectedOtherTORequests.requestUnit ?? "rooms"} requested
                  </Badge>
                </div>
              )}
            </div>
          </DialogHeader>

          {selectedOtherTORequests && (
            <div className="min-h-0 max-h-[70dvh] flex-1 overflow-y-auto overscroll-contain p-2 [scrollbar-gutter:stable] sm:p-4">
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <Table className="min-w-[760px] table-fixed">
                  <TableHeader className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700 whitespace-normal w-[20%]">Tour Name</TableHead>
                      <TableHead className="font-semibold text-gray-700 whitespace-normal w-[20%]">Tour Operator</TableHead>

                      {selectedOtherTORequests.requestUnit !== "applicants" && (
                        <TableHead className="font-semibold text-gray-700 text-center whitespace-normal w-[16%]">
                          Total Rooms
                        </TableHead>
                      )}
                      <TableHead className="font-semibold text-gray-700 text-center whitespace-normal w-[16%]">
                        {selectedOtherTORequests.requestUnit === "applicants" ? "Applicants" : "Rooms Requested"}
                      </TableHead>

                      <TableHead className="font-semibold text-gray-700 whitespace-normal w-[25%]">Requested Dates</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOtherTORequests.requests.map((req, reqIdx) => (
                      <TableRow
                        key={`${req.tourCode}-${reqIdx}`}
                        className="cursor-pointer hover:bg-sky-50/60"
                        onClick={() => {
                          setSelectedOtherTORequests(null);
                          setTourSwitchRequest(req);
                        }}
                      >
                        <TableCell className="whitespace-normal align-top">
                          <div className="font-semibold text-gray-900">{req.tourName || req.toName}</div>
                          <Badge variant="outline" className="bg-green-100 mt-1 font-mono text-xs">{req.tourCode}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700 whitespace-normal align-top break-words">
                          {req.toName}
                        </TableCell>

                        {selectedOtherTORequests.requestUnit !== "applicants" && (
                          <TableCell className="text-center whitespace-normal align-top">
                            <span className="inline-flex items-center justify-center min-w-10 rounded-full bg-purple-100 px-2.5 py-1 text-sm font-bold text-purple-700">
                              {req.totalRequestedRooms ?? 0}
                            </span>
                          </TableCell>
                        )}
                        <TableCell className="text-center whitespace-normal align-top">
                          <span className="inline-flex items-center justify-center min-w-10 rounded-full bg-purple-100 px-2.5 py-1 text-sm font-bold text-purple-700">
                            {getRequestAmount(req)}
                          </span>
                        </TableCell>

                        <TableCell className="text-sm text-gray-600 whitespace-normal align-top">
                          <div className="flex flex-wrap gap-1.5">
                            {req.dates.map((date) => (
                              <span key={date} className="rounded-md border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                                {formatShortDate(date)}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!tourSwitchRequest}
        onOpenChange={(open) => !open && setTourSwitchRequest(null)}
        title="Switch tour details?"
        description={tourSwitchRequest
          ? `Are you sure you want to switch to ${tourSwitchRequest.tourName || "the selected tour"} (${tourSwitchRequest.tourCode}) details?`
          : "Are you sure you want to switch the tour details?"}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmVariant="default"
        show={false}
        onConfirm={handleConfirmTourSwitch}
      />

      {/* Recommend Dates Dialog */}
      {showRecommendDialog && recommendationMode && (
        <RecommendDatesDialog
          tourId={tourId}
          mode={recommendationMode}
          startDate={tour?.requestedStartDate ?? tour?.startDate ?? ""}
          nights={Number(tour?.nights)}
          availabilityByDate={recommendedDates}
          onMonthChange={fetchRecommendedDates}
          onClose={() => {
            setShowRecommendDialog(false);
            setRecommendationMode(null);
          }}
          onConfirm={handleRecommendDates}
          isSubmittingRecommendation={isSubmittingRecommendation}
          isLoadingMonth={loadingCalendarMonth}
          tourStartDate={tour?.startDate ?? ""}
          tourEndDate={tour?.endDate ?? ""}
          venues={tour?.venues ?? []}
        />
      )}
    </div >
  );
}
